import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useThemeMode } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const FiltersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 20,
  flexWrap: 'wrap'
}));

const StyledSearchField = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#2A2A2A',
  borderRadius: 24,
  minWidth: 280,
  '& .MuiOutlinedInput-root': {
    borderRadius: 24,
    border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
    '& fieldset': {
      border: 'none'
    },
    '&:hover': {
      border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.15)'}`
    },
    '&.Mui-focused': {
      border: `1px solid #5073F8`
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: 14
  }
}));

const FilterButton = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 24,
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#2A2A2A',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)'
  }
}));

const HiddenScootersBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 16px',
  borderRadius: 24,
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#2A2A2A',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
  fontSize: 14
}));

interface Props {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  showHidden?: boolean;
  onShowHiddenChange?: (show: boolean) => void;
  showZones?: boolean;
  onShowZonesChange?: (show: boolean) => void;
  minBatteryLevel?: number;
  onMinBatteryLevelChange?: (level: number) => void;
}

const MapFiltersBar = ({
  searchValue = '',
  onSearchChange,
  showHidden = false,
  onShowHiddenChange,
  showZones = true,
  onShowZonesChange,
  minBatteryLevel = 0,
  onMinBatteryLevelChange
}: Props) => {
  const { mode } = useThemeMode();
  const { t } = useTranslation();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [tempBatteryLevel, setTempBatteryLevel] = useState(minBatteryLevel);

  const handleApplyFilters = () => {
    onMinBatteryLevelChange?.(tempBatteryLevel);
    setFilterDialogOpen(false);
  };

  const handleResetFilters = () => {
    setTempBatteryLevel(0);
    onMinBatteryLevelChange?.(0);
  };

  return (
    <>
      <FiltersContainer>
        <StyledSearchField
          placeholder={t('control.searchScooterPlaceholder')}
          value={searchValue}
          onChange={(e) => onSearchChange?.(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          size="small"
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <FilterButton onClick={() => setFilterDialogOpen(true)}>
            <FilterListIcon sx={{ color: mode === 'light' ? '#000000' : 'inherit' }} />
          </FilterButton>

          <HiddenScootersBox>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showHidden}
                  onChange={(e) => onShowHiddenChange?.(e.target.checked)}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&.Mui-checked': {
                      color: mode === 'light' ? '#3D5BD4' : '#5073F8'
                    }
                  }}
                />
              }
              label={t('control.showHidden')}
              sx={{ margin: 0 }}
            />
          </HiddenScootersBox>

          <HiddenScootersBox>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showZones}
                  onChange={(e) => onShowZonesChange?.(e.target.checked)}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&.Mui-checked': {
                      color: mode === 'light' ? '#3D5BD4' : '#5073F8'
                    }
                  }}
                />
              }
              label={t('control.showZones')}
              sx={{ margin: 0 }}
            />
          </HiddenScootersBox>
        </Box>
      </FiltersContainer>

      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 2
          }
        }}
      >
        <DialogTitle>{t('control.filterSettings')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>
              {t('control.minBatteryLevel', { level: tempBatteryLevel })}
            </Typography>
            <Slider
              value={tempBatteryLevel}
              onChange={(_, value) => setTempBatteryLevel(value as number)}
              min={0}
              max={100}
              step={5}
              marks={[
                { value: 0, label: '0%' },
                { value: 50, label: '50%' },
                { value: 100, label: '100%' }
              ]}
              valueLabelDisplay="auto"
              sx={{
                color: mode === 'light' ? '#3D5BD4' : '#5073F8',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#5073F8',
                  border: '2px solid #FFFFFF'
                },
                '& .MuiSlider-track': {
                  backgroundColor: mode === 'light' ? '#3D5BD4' : '#5073F8'
                },
                '& .MuiSlider-rail': {
                  backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleResetFilters} sx={{ color: 'text.secondary' }}>
            {t('common.reset')}
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="contained"
            sx={{
              backgroundColor: '#5073F8',
              color: '#000000',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              '&:hover': {
                backgroundColor: '#FDD835'
              }
            }}
          >
            {t('common.apply')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MapFiltersBar;
